import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ListCard from "../components/ListCard/ListCard";
import { useAuth } from "../context/AuthContext";
import { useSharedLists } from "../context/SharedListContext";
import { collection, getDocs, doc, deleteDoc, setDoc, updateDoc, arrayRemove } from "firebase/firestore";
import { db } from "../firebase/config";
import Button from "../components/Button/Button";
import Spinner from "../components/Spinner/Spinner";
import Modal from "../components/Modal/Modal";
import AddListForm from "../components/Forms/AddListForm";

export default function Lists() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { sharedLists, loading: sharedLoading } = useSharedLists();

    const [allLists, setAllLists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isAddListOpen, setAddListOpen] = useState(false);
    const [editList, setEditList] = useState(null);

    // Fetch lists from Firestore and combine with shared lists
    useEffect(() => {
        const fetchLists = async () => {
            try {
                const listsCol = collection(db, "lists");
                const snapshot = await getDocs(listsCol);
                const firestoreLists = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                }));

                // Merge with shared lists and remove duplicates by id
                const combinedLists = [...firestoreLists, ...sharedLists];
                const uniqueLists = combinedLists.filter(
                    (list, index, self) =>
                        index === self.findIndex(l => l.id === list.id)
                );

                // ✅ Filter to only show lists where user is a member
                const userLists = uniqueLists.filter(list =>
                    Array.isArray(list.members) && list.members.includes(user.uid)
                );

                setAllLists(userLists);
            } catch (err) {
                console.error("Error fetching lists:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchLists();
    }, [sharedLists, user.uid]);

    if (loading || sharedLoading) return <Spinner />;

    const handleSelectList = (list) => navigate(`/lists/${list.id}`);

    const handleDeleteList = async (list) => {
        if (!list || !list.id) {
            console.warn("Cannot delete list: missing list or list ID.", list);
            return;
        }

        try {
            const listRef = doc(db, "lists", list.id);

            if (list.ownerId === user.uid) {
                await deleteDoc(listRef);
                setAllLists(prev => prev.filter(l => l.id !== list.id));
            } else {
                const updates = {};
                if (Array.isArray(list.members)) {
                    updates.members = arrayRemove(user.uid);
                }

                if (Object.keys(updates).length > 0) {
                    await updateDoc(listRef, updates);
                }

                setAllLists(prev => prev.filter(l => l.id !== list.id));
            }
        } catch (err) {
            console.error("Error deleting list:", err);
        }
    };

    const handleAddList = async (newList) => {
        try {
            const listId = Date.now().toString();

            const listWithId = {
                ...newList,
                id: listId,
                ownerId: user.uid,
                items: [],
                invites: [],
                members: [user.uid],
                memberInfo: [
                    { uid: user.uid, displayName: user.displayName || user.email || "Unknown User" }
                ]
            };

            await setDoc(doc(db, "lists", listId), listWithId);
            setAllLists([...allLists, listWithId]);

            setAddListOpen(false);
            setEditList(null);
        } catch (err) {
            console.error("Error adding list:", err);
        }
    };

    const handleEditList = async (updatedList) => {
        try {
            const listRef = doc(db, "lists", updatedList.id);
            await updateDoc(listRef, {
                name: updatedList.name,
            });

            setAllLists(allLists.map(l => (l.id === updatedList.id ? updatedList : l)));
            setAddListOpen(false);
            setEditList(null);
        } catch (err) {
            console.error("Error updating list:", err);
        }
    };

    return (
        <div className="max-w-3xl mx-auto mt-10 min-h-full px-6 md:px-0">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-xl font-bold">Welcome, {user.firstName}!</h1>
                <Button
                    onClick={() => { setAddListOpen(true); setEditList(null); }}
                    variant="primary"
                >
                    Create New List
                </Button>
            </div>

            {/* Add/Edit List Modal */}
            {isAddListOpen && (
                <Modal
                    isOpen={isAddListOpen}
                    size="small"
                    onClose={() => {
                        setAddListOpen(false);
                        setEditList(null);
                    }}
                >
                    <AddListForm
                        addList={handleAddList}
                        editList={editList}
                        onClose={() => {
                            setAddListOpen(false);
                            setEditList(null);
                        }}
                        editListFn={handleEditList}
                    />
                </Modal>
            )}

            {/* ✅ Filtered Lists Display */}
            {allLists.length === 0 ? (
                <p className="text-center mt-8">
                    Get started by creating a list!
                </p>
            ) : (
                <div className="grid gap-4">
                    {allLists.map(list => {
                        const isAdmin = list.ownerId === user.uid;

                        return (
                            <ListCard
                                key={list.id}
                                list={list}
                                onSelect={handleSelectList}
                                onEdit={isAdmin ? () => { setEditList(list); setAddListOpen(true); } : null}
                                onDelete={() => handleDeleteList(list)}
                            />
                        );
                    })}
                </div>
            )}
        </div>
    );
}
